/**
 * Buy Now Button Handler for International Markets
 * 
 * Handles Buy Now button clicks on product pages:
 * 1. Adds product to cart
 * 2. Sends InitiateCheckout webhook to n8n
 * 3. Creates Stripe checkout session
 * 4. Redirects to Stripe Checkout
 */

(function() {
  'use strict';
  // Customer-facing copy comes from window.theme.strings (emitted by
  // snippets/head.liquid from the locale files) so it follows the storefront
  // language. The English argument stays as the fallback for any context where
  // theme.strings has not loaded.
  function themeStr(key, fallback) {
    var s = window.theme && window.theme.strings;
    return (s && s[key]) || fallback;
  }


  // Webhook Configuration
  const WEBHOOK_URL = 'https://n8n-latest-1-js6i.onrender.com/webhook/stripe-checkout/initiate';
  const WEBHOOK_TOKEN = 'IB98yh97b08G97Vvyf9797V7Vex5s34';
  
  /**
   * The shopper's market path, e.g. '/de-eu/' or '/ar-ae/', or '/' on India.
   * Market subfolders are always /{language}-{market}/.
   */
  function currentMarketPath() {
    var m = (window.location.pathname || '').toLowerCase()
      .match(/^\/([a-z]{2}-(?:ae|sa|gb|sg|us|eu|ca|au|my))(?:\/|$)/);
    return m ? '/' + m[1] + '/' : '/';
  }

  // Stripe Checkout API URLs. Test mode (?test_mode=true or localhost) routes to
  // the TEST worker, which only has the Stripe TEST key and whose test-mode
  // webhook feeds the separate n8n testing workflow. Live traffic is untouched.
  const STRIPE_CHECKOUT_API_URL = 'https://stripe-checkout.duavaa-checkout.workers.dev/v1/create-stripe-checkout';
  const STRIPE_CHECKOUT_TEST_API_URL = 'https://stripe-checkout-test.duavaa-checkout.workers.dev/v1/create-stripe-checkout';

  function getStripeCheckoutApiUrl(isTestMode) {
    return isTestMode ? STRIPE_CHECKOUT_TEST_API_URL : STRIPE_CHECKOUT_API_URL;
  }

  // Max distinct cart lines per checkout. Keep in sync with LARGE_ORDER_MAX_LINES
  // in tally-checkout.js (worker hard cap is 40; 25 leaves headroom).
  const BUY_NOW_MAX_LINES = 25;

  // Check if this is an international market
  function isInternationalMarket() {
    if (window.MarketConfig && typeof window.MarketConfig.isInternationalMarket === 'function') {
      return window.MarketConfig.isInternationalMarket();
    }
    
    if (window.WebhookHelper && typeof window.WebhookHelper.isInternationalMarket === 'function') {
      return window.WebhookHelper.isInternationalMarket();
    }
    
    const path = window.location.pathname.toLowerCase();
    // Fallback: treat all known international locales (including Canada & Australia) as international
    return /^\/(en-(?:ae|sa|gb|sg|us|eu|ca|au|my))(?:\/|$)/.test(path) ||
           /\/en-(?:ae|sa|gb|sg|us|eu|ca|au|my)(?:\/|$)/.test(path);
  }

  // Only run on international markets
  if (!isInternationalMarket()) {
    return;
  }

  // Reuse helper functions from tally-checkout.js and webhook-helper-international.js
  function getCartData() {
    // Use market-specific cart URL to ensure Shopify Markets returns correct catalog prices
    const { marketInfo } = getLocaleInfo();
    let cartUrl = '/cart.js';
    
    if (marketInfo && marketInfo.key !== 'default' && marketInfo.config.path) {
      // Use market-specific cart URL (e.g., /en-sg/cart.js)
      const marketPath = marketInfo.config.path.replace(/\/$/, ''); // Remove trailing slash
      cartUrl = marketPath + '/cart.js';
    }
    
    console.log('[DEBUG getCartData] Fetching cart from:', cartUrl);
    
    return fetch(cartUrl)
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch cart');
        return res.json();
      })
      .catch(() => null);
  }

  function formatProductGID(productId) {
    return `gid://shopify/Product/${productId}`;
  }

  function convertToMarketCurrency(priceInCents) {
    console.log('[DEBUG convertToMarketCurrency] Input priceInCents:', priceInCents);
    
    if (window.WebhookHelper && typeof window.WebhookHelper.convertToMarketCurrency === 'function') {
      console.log('[DEBUG convertToMarketCurrency] Using WebhookHelper.convertToMarketCurrency');
      const result = window.WebhookHelper.convertToMarketCurrency(priceInCents);
      console.log('[DEBUG convertToMarketCurrency] WebhookHelper result:', result);
      return result;
    }
    if (window.MarketConfig && typeof window.MarketConfig.convertPrice === 'function') {
      console.log('[DEBUG convertToMarketCurrency] Using MarketConfig.convertPrice');
      // Check what market is detected
      const detectedMarket = window.MarketConfig.detectMarket();
      console.log('[DEBUG convertToMarketCurrency] Detected market:', detectedMarket);
      console.log('[DEBUG convertToMarketCurrency] Market conversion rate:', detectedMarket.config?.conversionRate);
      const result = window.MarketConfig.convertPrice(priceInCents);
      console.log('[DEBUG convertToMarketCurrency] MarketConfig result:', result);
      return result;
    }
    // Fallback
    console.log('[DEBUG convertToMarketCurrency] Using fallback rate 0.04');
    const rate = 0.04;
    const inrAmount = priceInCents / 100;
    const result = parseFloat((inrAmount * rate).toFixed(2));
    console.log('[DEBUG convertToMarketCurrency] Fallback result:', result);
    return result;
  }

  function roundUpPrice(price) {
    if (window.WebhookHelper && typeof window.WebhookHelper.roundUpPrice === 'function') {
      return window.WebhookHelper.roundUpPrice(price);
    }
    return Math.ceil(price);
  }

  function getMetaPixelIdentifiers() {
    if (window.WebhookHelper) {
      return {
        fbp: window.WebhookHelper.getFbpCookie(),
        fbc: window.WebhookHelper.getFbcCookie()
      };
    }
    
    const identifiers = { fbp: null, fbc: null };
    try {
      const fbpMatch = document.cookie.match(/(?:^|; )_fbp=([^;]+)/);
      if (fbpMatch && fbpMatch[1]) {
        identifiers.fbp = decodeURIComponent(fbpMatch[1]);
      }

      const fbcMatch = document.cookie.match(/(?:^|; )_fbc=([^;]+)/);
      if (fbcMatch && fbcMatch[1]) {
        const fbc = decodeURIComponent(fbcMatch[1]);
        if (!fbc.endsWith('.fbclid') && fbc.split('.').length >= 4) {
          identifiers.fbc = fbc;
        }
      }
    } catch (e) {
      // Silently ignore cookie reading errors
    }
    return identifiers;
  }

  function generateEventId() {
    if (window.WebhookHelper && typeof window.WebhookHelper.generateEventId === 'function') {
      return window.WebhookHelper.generateEventId();
    }
    function generateHex(length) {
      return Array.from({length: length}, () => 
        Math.floor(Math.random() * 16).toString(16).toUpperCase()
      ).join('');
    }
    return 'sh-' + generateHex(8) + '-' + generateHex(4) + '-' + 
           generateHex(4) + '-' + generateHex(4) + '-' + generateHex(12);
  }

  // Detect market and locale info
  function getLocaleInfo() {
    console.log('[DEBUG getLocaleInfo] Starting market detection...');
    console.log('[DEBUG getLocaleInfo] Current URL:', window.location.href);
    console.log('[DEBUG getLocaleInfo] Current pathname:', window.location.pathname);
    console.log('[DEBUG getLocaleInfo] window.MarketConfig exists:', !!window.MarketConfig);
    console.log('[DEBUG getLocaleInfo] window.WebhookHelper exists:', !!window.WebhookHelper);
    console.log('[DEBUG getLocaleInfo] window.shopifyLocale:', window.shopifyLocale);
    
    let marketInfo = null;
    let localeInfo = { currency: 'AED', market: 'UAE' };
    
    if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
      console.log('[DEBUG getLocaleInfo] Using MarketConfig.getCurrentMarket()');
      marketInfo = window.MarketConfig.getCurrentMarket();
      console.log('[DEBUG getLocaleInfo] MarketConfig.getCurrentMarket() result:', marketInfo);
      if (marketInfo && marketInfo.config) {
        localeInfo = {
          currency: marketInfo.config.currency,
          market: marketInfo.config.market,
          locale: marketInfo.key || marketInfo.config.locale
        };
        console.log('[DEBUG getLocaleInfo] Extracted localeInfo from MarketConfig:', localeInfo);
      }
    } else if (window.WebhookHelper && typeof window.WebhookHelper.getMarketConfig === 'function') {
      console.log('[DEBUG getLocaleInfo] Using WebhookHelper.getMarketConfig()');
      const marketConfig = window.WebhookHelper.getMarketConfig();
      console.log('[DEBUG getLocaleInfo] WebhookHelper.getMarketConfig() result:', marketConfig);
      localeInfo = {
        currency: marketConfig.currency,
        market: marketConfig.market,
        locale: marketConfig.locale
      };
      console.log('[DEBUG getLocaleInfo] Extracted localeInfo from WebhookHelper:', localeInfo);
    } else {
      console.log('[DEBUG getLocaleInfo] Using fallback URL detection');
      // Fallback: detect from URL
      const path = window.location.pathname.toLowerCase();
      console.log('[DEBUG getLocaleInfo] Checking path:', path);
      // Market subfolders are always {language}-{market}, so resolve on the
      // suffix rather than listing every language-market pair.
      const MARKETS_BY_SUFFIX = {
        ae: { currency: 'AED', market: 'UAE' },
        sa: { currency: 'SAR', market: 'Saudi Arabia' },
        gb: { currency: 'GBP', market: 'UK' },
        sg: { currency: 'SGD', market: 'Singapore' },
        my: { currency: 'MYR', market: 'Malaysia' },
        us: { currency: 'USD', market: 'USA' },
        eu: { currency: 'EUR', market: 'Europe' },
        ca: { currency: 'CAD', market: 'Canada' },
        au: { currency: 'AUD', market: 'Australia' }
      };
      const marketMatch = path.match(/(?:^|\/)([a-z]{2}-(ae|sa|gb|sg|us|eu|ca|au|my))(?:\/|$)/);
      if (marketMatch && MARKETS_BY_SUFFIX[marketMatch[2]]) {
        const hit = MARKETS_BY_SUFFIX[marketMatch[2]];
        localeInfo = { currency: hit.currency, market: hit.market, locale: marketMatch[1] };
      }
      console.log('[DEBUG getLocaleInfo] Fallback localeInfo:', localeInfo);
    }
    
    console.log('[DEBUG getLocaleInfo] Final result - marketInfo:', marketInfo, 'localeInfo:', localeInfo);
    return { marketInfo, localeInfo };
  }

  // Collect contact details from user
  function collectContactDetails() {
    return new Promise((resolve, reject) => {
      const contactSheet = document.getElementById('contact-details-sheet');
      const dialog = document.getElementById('contact-details-dialog');
      
      if (!contactSheet || !dialog) {
        console.warn('[Buy Now] Contact details sheet not found');
        // If sheet doesn't exist, proceed without contact details
        resolve({ email: '', phone: '' });
        return;
      }

      let formSubmitted = false;

      // Set up event listener for form submission
      const handleContactCollected = (event) => {
        formSubmitted = true;
        const formData = event.detail;
        cleanup();
        resolve({
          email: formData.email || '',
          phone: formData.phone || ''
        });
      };

      // Set up event listener for dialog close (user cancelled)
      const handleDialogClose = () => {
        // If dialog closes without form submission, user cancelled
        if (!formSubmitted) {
          cleanup();
          reject(new Error('User cancelled contact details collection'));
        }
      };

      // Cleanup function
      const cleanup = () => {
        document.removeEventListener('contactDetailsCollected', handleContactCollected);
        dialog.removeEventListener('close', handleDialogClose);
      };

      // Listen for form submission (must be before opening dialog)
      document.addEventListener('contactDetailsCollected', handleContactCollected, { once: true });

      // Listen for dialog close
      dialog.addEventListener('close', handleDialogClose, { once: true });

      // Open the contact details sheet
      try {
        if (contactSheet.popupOpen && typeof contactSheet.popupOpen === 'function') {
          contactSheet.popupOpen();
        } else if (dialog.showModal && typeof dialog.showModal === 'function') {
          dialog.showModal();
          dialog.removeAttribute('inert');
          dialog.setAttribute('aria-hidden', 'false');
        } else {
          dialog.setAttribute('open', '');
          dialog.removeAttribute('inert');
          dialog.setAttribute('aria-hidden', 'false');
        }
      } catch (error) {
        console.error('[Buy Now] Error opening contact details sheet:', error);
        cleanup();
        // If we can't open the sheet, proceed without contact details
        resolve({ email: '', phone: '' });
      }
    });
  }

  // Build checkout redirect URL (same format as tally-checkout.js)
  function buildCheckoutRedirectURL(cart, localeInfo, contactDetails = {}) {
    const { marketInfo } = getLocaleInfo();
    
    // Build base URL
    let baseURL = '/pages/checkout-redirect';
    if (marketInfo && marketInfo.key !== 'default' && marketInfo.config.path) {
      // Normalize path: remove trailing slash, ensure it starts with /
      let marketPath = marketInfo.config.path.trim();
      if (marketPath && !marketPath.startsWith('/')) {
        marketPath = '/' + marketPath;
      }
      marketPath = marketPath.replace(/\/+$/, ''); // Remove trailing slashes
      baseURL = marketPath + '/pages/checkout-redirect';
    } else {
      // Fallback: detect from URL path if MarketConfig is not available
      const pathMatch = window.location.pathname.match(/^\/(en-[a-z]{2})(?:\/|$)/);
      if (pathMatch) {
        baseURL = pathMatch[1] + '/pages/checkout-redirect';
      }
    }
    
    const params = new URLSearchParams();
    
    params.set('total_items', cart.items.length.toString());
    
    // Add product data and calculate subtotal (guard already capped the cart)
    const maxItems = Math.min(cart.items.length, BUY_NOW_MAX_LINES);
    let calculatedSubtotal = 0;

    // ---- Offer Engine (international): compute per-item discounted unit prices ----
    const offerBaseItems = [];
    for (let oi = 0; oi < maxItems; oi++) {
      const oit = cart.items[oi];
      const oprops = oit.properties || {};
      offerBaseItems.push({
        key: String(oit.variant_id),
        quantity: oit.quantity,
        unitPrice: roundUpPrice(convertToMarketCurrency(oit.final_price)),
        isMto: !!oprops._Service // only the plating service FEE line; the plated product itself stays offer-eligible
      });
    }
    const offerResult = (window.OfferEngine && typeof window.OfferEngine.apply === 'function')
      ? window.OfferEngine.apply(offerBaseItems)
      : { applied: false, totalDiscount: 0, offerLabel: '', items: offerBaseItems.map(function (x) { return { discountedUnitPrice: x.unitPrice }; }) };

    for (let i = 0; i < maxItems; i++) {
      const item = cart.items[i];
      const index = i + 1;
      
      params.set(`product_gid_${index}`, formatProductGID(item.product_id));
      params.set(`variant_id_${index}`, item.variant_id.toString());
      params.set(`quantity_${index}`, item.quantity.toString());
      
      // Use final_price (unit price) - Stripe multiplies by quantity.
      // Offer Engine may have discounted this line for the current country.
      const baseUnitPrice = roundUpPrice(convertToMarketCurrency(item.final_price));
      const unitPrice = (offerResult.items[i] && offerResult.items[i].discountedUnitPrice != null)
        ? offerResult.items[i].discountedUnitPrice
        : baseUnitPrice;
      params.set(`price_${localeInfo.currency.toLowerCase()}_${index}`, unitPrice.toFixed(2));
      // Keep price_aed for backward compatibility
      params.set(`price_aed_${index}`, unitPrice.toFixed(2));
      
      // Calculate line price (unit price * quantity) and add to subtotal
      const linePrice = unitPrice * item.quantity;
      calculatedSubtotal += linePrice;
      
      // Also add line price for reference
      params.set(`line_price_${localeInfo.currency.toLowerCase()}_${index}`, linePrice.toFixed(2));
      
      // Add product name for Stripe checkout display
      const productName = item.title || item.product_title || '';
      const variantTitle = item.variant_title || '';
      const displayName = variantTitle ? `${productName} - ${variantTitle}` : productName;
      if (displayName) {
        params.set(`product_name_${index}`, displayName);
      }
      if (window.CartLineItemProperties && typeof window.CartLineItemProperties.jsonForUrlParam === 'function') {
        const propsJson = window.CartLineItemProperties.jsonForUrlParam(item);
        if (propsJson) {
          params.set(`line_item_properties_${index}`, propsJson);
        }
      }
    }
    
    // Set subtotal as sum of rounded line prices
    params.set('subtotal', calculatedSubtotal.toFixed(2));

    // Offer Engine: pass the applied offer so n8n can tag the order (approach A — lines already discounted)
    if (offerResult.applied) {
      params.set('discount_code', offerResult.offerLabel || '');
      params.set('discount_amount', offerResult.totalDiscount.toFixed(2));
    }

    // Add email to URL params (for checkout-redirect page to pass to Stripe)
    if (contactDetails.email) {
      params.set('email', contactDetails.email);
    }
    
    const checkoutURL = `${baseURL}?${params.toString()}`;
    
    // If in preview mode, preserve the preview parameters
    let finalURL = checkoutURL;
    if (window.location.href.includes('/preview/')) {
      const previewMatch = window.location.href.match(/\/preview\/[^\/]+/);
      if (previewMatch) {
        finalURL = previewMatch[0] + checkoutURL;
      }
    }
    
    return finalURL;
  }

  // Build webhook payload (reuse from tally-checkout.js)
  function buildWebhookPayload(cart, hiddenFields, localeInfo, contactDetails = {}, checkoutUrl = null) {
    let marketInfo = null;
    let catalogPrefix = '';
    let gateway = 'stripe';
    
    if (window.MarketConfig && typeof window.MarketConfig.getCurrentMarket === 'function') {
      marketInfo = window.MarketConfig.getCurrentMarket();
      if (marketInfo && marketInfo.config) {
        catalogPrefix = marketInfo.config.catalogPrefix || '';
        gateway = marketInfo.config.gateway || 'stripe';
      }
    }
    
    // Build content_ids - use helper if available
    const variantIds = cart.items.map(item => item.variant_id.toString());
    let contentIds = variantIds;
    if (window.WebhookHelper && typeof window.WebhookHelper.prefixContentIds === 'function') {
      contentIds = window.WebhookHelper.prefixContentIds(variantIds);
    } else if (window.MarketConfig && typeof window.MarketConfig.prefixContentIds === 'function') {
      contentIds = window.MarketConfig.prefixContentIds(variantIds);
    } else if (catalogPrefix) {
      contentIds = variantIds.map(id => id.startsWith(catalogPrefix) ? id : catalogPrefix + id);
    }
    
    // Build contents array
    const contents = cart.items.map((item, index) => {
      const variantId = item.variant_id.toString();
      const prefixedVariantId = contentIds[index] || variantId;
      const unitPrice = roundUpPrice(convertToMarketCurrency(item.final_price));
      
      return {
        id: prefixedVariantId,
        sku: prefixedVariantId,
        item_price: unitPrice,
        quantity: item.quantity,
        currency: localeInfo.currency
      };
    });
    
    // Calculate subtotal as sum of rounded line prices (unit price * quantity for each item)
    // This ensures the total matches the sum of individual rounded prices
    const subtotal = contents.reduce((sum, item) => {
      return sum + (item.item_price * item.quantity);
    }, 0);
    
    // Build user_data for Meta Pixel (include contact details if available)
    const userData = {
      client_user_agent: navigator.userAgent,
      client_ip_address: null
    };

    // Add contact details to payload
    const payload = {
      custom_data: {
        value: subtotal,
        currency: localeInfo.currency,
        content_type: 'product_group',
        content_ids: contentIds,
        num_items: cart.items.length,
        market: localeInfo.market,
        checkout_type: 'buy_now',
        contents: contents,
        gateway: gateway,
        catalog_prefix: catalogPrefix,
        locale: localeInfo.locale || localeInfo.market.toLowerCase()
      },
      cart_data: {
        total_price: cart.total_price,
        total_price_converted: subtotal,
        item_count: cart.items.length,
        items: cart.items.map((item, index) => ({
          product_id: item.product_id,
          variant_id: item.variant_id,
          quantity: item.quantity,
          final_price: item.final_price,
          final_price_converted: roundUpPrice(convertToMarketCurrency(item.final_price)),
          title: item.title || item.product_title || ''
        }))
      },
      user_data: userData,
      // Add plain contact details for n8n processing
      contact_details: {
        email: contactDetails.email || null,
        phone: contactDetails.phone || null
      },
      // Add checkout redirect URL if available
      checkout_url: checkoutUrl || null
    };
    
    return payload;
  }

  // Add product to cart (bundles gold plating add-on line when checkbox + data attribute are set)
  async function addProductToCart(form) {
    // Use market-specific cart URL to ensure Shopify Markets uses correct catalog
    const { marketInfo } = getLocaleInfo();
    let cartAddUrl = '/cart/add.js';

    if (marketInfo && marketInfo.key !== 'default' && marketInfo.config.path) {
      const marketPath = marketInfo.config.path.replace(/\/$/, '');
      cartAddUrl = marketPath + '/cart/add.js';
    }

    console.log('[DEBUG addProductToCart] Adding to cart via:', cartAddUrl);
    console.log('[DEBUG addProductToCart] Market context:', marketInfo);

    const jsonBody =
      window.GoldPlatingCart && typeof window.GoldPlatingCart.buildMultiItemBody === 'function'
        ? window.GoldPlatingCart.buildMultiItemBody(form)
        : null;

    try {
      const response = await fetch(cartAddUrl, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
          ...(jsonBody ? { 'Content-Type': 'application/json' } : {}),
        },
        body: jsonBody || new FormData(form),
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok || (result && result.status)) {
        throw new Error(
          result.description || result.message || 'Failed to add product to cart'
        );
      }

      return result;
    } catch (error) {
      console.error('[Buy Now] Error adding to cart:', error);
      throw error;
    }
  }

  // REMOVED: waitForCartUpdate function - no longer needed
  // We trust the /cart/add.js response and fetch cart immediately (like India implementation)

  // Send InitiateCheckout webhook (fire and forget - never blocks)
  function sendInitiateCheckoutWebhook(cart, localeInfo, contactDetails = {}, checkoutUrl = null) {
    try {
      const hiddenFields = {};
      const payload = buildWebhookPayload(cart, hiddenFields, localeInfo, contactDetails, checkoutUrl);
      
      // Use helper if available (for international markets), otherwise use manual fetch
      if (window.WebhookHelper && window.WebhookHelper.isInternationalMarket && window.WebhookHelper.isInternationalMarket()) {
        // Use helper's sendWebhook (fire and forget - doesn't return promise)
        window.WebhookHelper.sendWebhook('InitiateCheckout', payload);
      } else {
        // Fallback: manual fetch for India or when helper not available
        const eventId = generateEventId();
        const eventTime = Math.floor(Date.now() / 1000);
        const metaPixelIds = getMetaPixelIdentifiers();
        
        const fullPayload = {
          ...payload,
          event_name: 'InitiateCheckout',
          event_time: eventTime,
          event_id: eventId,
          event_source_url: window.location.href,
          action_source: 'website',
          fbp: metaPixelIds.fbp,
          fbc: metaPixelIds.fbc,
          user_data: {
            client_user_agent: navigator.userAgent,
            client_ip_address: null
          }
        };
        
        fetch(WEBHOOK_URL, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Webhook-Token': WEBHOOK_TOKEN
          },
          body: JSON.stringify(fullPayload),
          keepalive: true
        }).catch(() => {
          // Silent failure - don't block user experience
        });
      }
    } catch (error) {
      // Silent failure - webhook should never block checkout
    }
  }

  // Detect test mode (same logic as checkout-redirect.liquid)
  function detectTestMode() {
    const DEFAULT_TEST_MODE = false; // Live mode enabled - change to true for test mode
    
    const urlParams = new URLSearchParams(window.location.search);
    const urlTestMode = urlParams.get('test_mode');
    
    // Priority: URL parameter > localhost detection > default config
    let isTestMode = DEFAULT_TEST_MODE;
    
    // Override with URL parameter if explicitly set
    if (urlTestMode === 'true' || urlTestMode === '1') {
      isTestMode = true;
    } else if (urlTestMode === 'false' || urlTestMode === '0') {
      isTestMode = false;
    }
    // Theme setting (Customize > International Market Settings > Checkout test
    // mode): sitewide test mode on an unpublished theme, no URL param needed.
    else if (window.DuavaaCheckoutTestMode === true) {
      isTestMode = true;
    }
    // Override with localhost detection (if URL param not set)
    else if (typeof window !== 'undefined' &&
             (window.location.hostname.includes('localhost') ||
              window.location.hostname.includes('127.0.0.1'))) {
      isTestMode = true;
    }
    // Otherwise use DEFAULT_TEST_MODE
    
    return isTestMode;
  }

  // Create Stripe checkout session
  async function createStripeCheckoutSession(cart, localeInfo, contactDetails = {}) {
    console.log('[DEBUG createStripeCheckoutSession] Starting...');
    console.log('[DEBUG createStripeCheckoutSession] Received localeInfo:', localeInfo);
    console.log('[DEBUG createStripeCheckoutSession] Cart items count:', cart.items.length);
    
    const { marketInfo } = getLocaleInfo();
    console.log('[DEBUG createStripeCheckoutSession] MarketInfo from getLocaleInfo():', marketInfo);
    
    // ---- Offer Engine (international): discounted unit prices for the current country ----
    const __offerItems = cart.items.slice(0, BUY_NOW_MAX_LINES).map(function (it) {
      var p = (window.CartLineItemProperties && typeof window.CartLineItemProperties.toPlainObject === 'function') ? window.CartLineItemProperties.toPlainObject(it) : (it.properties || {});
      return {
        key: String(it.variant_id),
        quantity: it.quantity,
        unitPrice: roundUpPrice(convertToMarketCurrency(it.final_price)),
        isMto: !!(p && p._Service) // only the plating service FEE line; the plated product itself stays offer-eligible
      };
    });
    const __offer = (window.OfferEngine && typeof window.OfferEngine.apply === 'function')
      ? window.OfferEngine.apply(__offerItems)
      : { applied: false, totalDiscount: 0, offerLabel: '', items: __offerItems.map(function (x) { return { discountedUnitPrice: x.unitPrice }; }) };

    // Build products array
    // IMPORTANT: Stripe API expects unit price (final_price), not line price (line_price)
    // The API will multiply unit price by quantity automatically
    const products = cart.items.slice(0, BUY_NOW_MAX_LINES).map((item, __oidx) => {
      console.log('[DEBUG createStripeCheckoutSession] Processing item:', {
        variant_id: item.variant_id,
        product_id: item.product_id,
        final_price_inr_cents: item.final_price,
        line_price: item.line_price,
        quantity: item.quantity
      });
      
      // Use final_price (unit price) - Stripe will multiply by quantity.
      // Offer Engine may have discounted this line for the current country (international only).
      const baseUnitPrice = roundUpPrice(convertToMarketCurrency(item.final_price));
      const unitPrice = (__offer.items[__oidx] && __offer.items[__oidx].discountedUnitPrice != null)
        ? __offer.items[__oidx].discountedUnitPrice
        : baseUnitPrice;
      const productName = item.title || item.product_title || '';
      const variantTitle = item.variant_title || '';
      const displayName = variantTitle ? `${productName} - ${variantTitle}` : productName;
      
      // Log each product being sent to Stripe for debugging
      console.log('[Buy Now] Product for Stripe checkout:', {
        variant_id: item.variant_id,
        product_id: item.product_id,
        quantity: item.quantity,
        final_price_inr_cents: item.final_price,
        unit_price_market_currency: unitPrice,
        displayName: displayName,
        expected_market: localeInfo.currency,
        conversion_applied: unitPrice !== (item.final_price / 100)
      });
      
      const lineItem = {
        product_gid: formatProductGID(item.product_id),
        variant_id: item.variant_id.toString(),
        quantity: item.quantity.toString(),
        price: unitPrice.toString(), // Unit price - Stripe multiplies by quantity
        name: displayName
      };
      if (window.CartLineItemProperties && typeof window.CartLineItemProperties.toPlainObject === 'function') {
        const props = window.CartLineItemProperties.toPlainObject(item);
        if (Object.keys(props).length) {
          lineItem.line_item_properties = props;
        }
      }
      return lineItem;
    });

    // Calculate subtotal as sum of rounded line prices (unit price * quantity for each item)
    // This ensures the total matches the sum of individual rounded prices
    const subtotal = products.reduce((sum, product) => {
      return sum + (parseFloat(product.price) * parseFloat(product.quantity));
    }, 0);
    const metaPixelIds = getMetaPixelIdentifiers();
    
    // Detect test mode (same logic as checkout-redirect.liquid)
    const isTestMode = detectTestMode();

    // Attribution visitor id, resolved rather than read: a shopper who lands
    // and clicks Buy Now immediately can beat the collect round-trip, and the
    // global is empty exactly then. Falls back to null; checkout never waits
    // more than the resolver's timeout.
    const attribVid = window.__duavaaGetVid
      ? await window.__duavaaGetVid(500)
      : (window.__duavaaVid || null);

    const requestPayload = {
      subtotal: subtotal.toString(),
      email: contactDetails.email || '',
      phone: '', // Phone not needed for Stripe checkout
      total_items: cart.items.length.toString(),
      products: products,
      market: localeInfo.market,
      currency: localeInfo.currency,
      test_mode: isTestMode,
      fbp: metaPixelIds.fbp || null,
      fbc: metaPixelIds.fbc || null,
      // Attribution visitor id (resolved above). Rides exactly the path
      // fbp/fbc already do: here, then the worker's Stripe session metadata,
      // then n8n onto the order as the _duavaa_vid note attribute. A missing
      // id costs one order's attribution; a wrong one poisons a journey.
      vid: attribVid,
      base_url: window.location.origin,
      // Market path the shopper is on ('/de-eu/', '/ar-ae/', or '/' on India),
      // so Stripe returns them to their own language after paying.
      locale_path: currentMarketPath(),
      // Offer Engine (approach A): lines above are already discounted, so the total
      // reconciles automatically. These are for order tagging / reporting only.
      discount_code: __offer.applied ? (__offer.offerLabel || '') : '',
      discount_amount: __offer.applied ? __offer.totalDiscount.toFixed(2) : '0'
    };

    console.log('[Buy Now] Creating Stripe checkout session...', requestPayload);

    // Add timeout to prevent hanging (10 seconds max)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);

    const response = await fetch(getStripeCheckoutApiUrl(isTestMode), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestPayload),
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: errorText || `HTTP ${response.status}` };
      }
      throw new Error(errorData.error || errorData.message || 'Failed to create checkout session');
    }

    const checkoutData = await response.json();
    
    if (!checkoutData.url) {
      throw new Error('Checkout URL not received from server');
    }

    return checkoutData.url;
  }

  // Show loading state
  function showButtonLoading(button) {
    button.disabled = true;
    button.dataset.processing = 'true';
    button.classList.add('is-loading');
    
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    
    if (textWrapper) {
      textWrapper.style.opacity = '0.5';
    }
    
    if (loader) {
      loader.style.opacity = '1';
      loader.style.display = 'block';
    }
  }

  // Hide loading state
  function hideButtonLoading(button) {
    button.disabled = false;
    button.dataset.processing = 'false';
    button.classList.remove('is-loading');
    
    const textWrapper = button.querySelector('.btn__text');
    const loader = button.querySelector('.btn__loader');
    
    if (textWrapper) {
      textWrapper.style.opacity = '1';
    }
    
    if (loader) {
      loader.style.opacity = '0';
      loader.style.display = 'none';
    }
  }

  // Main handler function
  async function handleBuyNow(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    console.log('[DEBUG handleBuyNow] ========== BUY NOW CLICKED ==========');
    console.log('[DEBUG handleBuyNow] Current URL:', window.location.href);
    console.log('[DEBUG handleBuyNow] Current pathname:', window.location.pathname);
    console.log('[DEBUG handleBuyNow] window.MarketConfig exists:', !!window.MarketConfig);
    console.log('[DEBUG handleBuyNow] window.MarketConfig.getCurrentMarket exists:', !!(window.MarketConfig && window.MarketConfig.getCurrentMarket));
    if (window.MarketConfig) {
      console.log('[DEBUG handleBuyNow] MarketConfig.getCurrentMarket():', window.MarketConfig.getCurrentMarket());
      console.log('[DEBUG handleBuyNow] MarketConfig.detectMarket():', window.MarketConfig.detectMarket());
    }
    console.log('[DEBUG handleBuyNow] window.WebhookHelper exists:', !!window.WebhookHelper);
    console.log('[DEBUG handleBuyNow] window.shopifyLocale:', window.shopifyLocale);

    const button = event.currentTarget || event.target || event.target.closest('[data-buy-now-button]');
    
    if (!button) {
      console.warn('[Buy Now] No button found');
      return;
    }

    // Prevent multiple clicks
    if (button.disabled || button.dataset.processing === 'true') {
      return;
    }

    showButtonLoading(button);

    try {
      // Get product form
      const form = button.closest('form[data-product-form]') || button.closest('form.product-form');
      if (!form) {
        throw new Error('Product form not found');
      }

      // Extract variant ID - check multiple sources to ensure we get the correct one
      // 1. First, try input inside the form
      // 2. Then try input with form attribute matching form ID
      // 3. Finally, use FormData (which respects form attribute)
      let variantId = null;
      const formId = form.id;
      
      // Method 1: Input inside form
      const variantInputInForm = form.querySelector('input[name="id"]');
      if (variantInputInForm && variantInputInForm.value) {
        variantId = variantInputInForm.value;
      }
      
      // Method 2: Input with form attribute (may be outside form element)
      if (!variantId && formId) {
        const variantInputWithFormAttr = document.querySelector(`input[name="id"][form="${formId}"]`);
        if (variantInputWithFormAttr && variantInputWithFormAttr.value) {
          variantId = variantInputWithFormAttr.value;
        }
      }
      
      // Method 3: Any input with name="id" in form wrapper
      if (!variantId) {
        const formWrapper = form.closest('[data-form-wrapper]');
        if (formWrapper) {
          const variantInputInWrapper = formWrapper.querySelector('input[name="id"]');
          if (variantInputInWrapper && variantInputInWrapper.value) {
            variantId = variantInputInWrapper.value;
          }
        }
      }
      
      // Method 4: Fallback to FormData (which should handle form attribute correctly)
      if (!variantId) {
        const formData = new FormData(form);
        variantId = formData.get('id') || formData.get('variant_id');
      }
      
      if (!variantId) {
        console.error('[Buy Now] ❌ Variant ID not found. Form:', form, 'Form ID:', formId);
        throw new Error('Variant ID not found in form. Please select a variant and try again.');
      }
      
      // Also get quantity from form (check same sources)
      let quantity = 1;
      const quantityInputInForm = form.querySelector('input[name="quantity"]');
      if (quantityInputInForm && quantityInputInForm.value) {
        quantity = parseInt(quantityInputInForm.value, 10) || 1;
      } else if (formId) {
        const quantityInputWithFormAttr = document.querySelector(`input[name="quantity"][form="${formId}"]`);
        if (quantityInputWithFormAttr && quantityInputWithFormAttr.value) {
          quantity = parseInt(quantityInputWithFormAttr.value, 10) || 1;
        }
      }
      
      console.log('[Buy Now] Extracted form data:', { 
        variantId, 
        quantity,
        formId: formId,
        formAction: form.action
      });

      // Add product to cart
      console.log('[Buy Now] Adding product to cart...', { variantId, quantity });
      const addResult = await addProductToCart(form);
      
      if (addResult.status) {
        // Error adding to cart
        hideButtonLoading(button);
        alert(addResult.description || themeStr('buyNowAddFailed', 'Unable to add product to cart. Please try again.'));
        return;
      }

      // FAST PATH: Fetch cart immediately (single fetch, no polling, no delays)
      // This matches the India implementation speed
      console.log('[Buy Now] ✅ Product added to cart, fetching cart data...');
      const cart = await getCartData();
      
      console.log('[DEBUG handleBuyNow] Cart data received:', {
        total_items: cart?.items?.length,
        total_price: cart?.total_price,
        items: cart?.items?.map(item => ({
          variant_id: item.variant_id,
          product_id: item.product_id,
          final_price: item.final_price,
          line_price: item.line_price,
          quantity: item.quantity,
          title: item.title
        }))
      });
      
      if (!cart || !cart.items || cart.items.length === 0) {
        throw new Error('Cart is empty after adding product');
      }

      // Buy Now checks out the WHOLE cart (this product + existing items), so
      // it needs the same large-order guard as the cart checkout. The modal is
      // provided by tally-checkout.js (loaded globally after this script).
      if (cart.items.length > BUY_NOW_MAX_LINES) {
        hideButtonLoading(button);
        if (window.DuavaaLargeOrderModal && typeof window.DuavaaLargeOrderModal.show === 'function') {
          window.DuavaaLargeOrderModal.show(cart);
        } else {
          alert(themeStr('buyNowCartTooLarge', 'Your cart has more items than online checkout supports. Please contact us on WhatsApp or at hello@duavaa.com and we will arrange this order for you.'));
        }
        return;
      }

      // Get locale info (do this before webhook to avoid any delay)
      console.log('[DEBUG handleBuyNow] Detecting market/locale info...');
      const { localeInfo, marketInfo } = getLocaleInfo();
      console.log('[DEBUG handleBuyNow] Detected localeInfo:', localeInfo);
      console.log('[DEBUG handleBuyNow] Detected marketInfo:', marketInfo);

      // Send InitiateCheckout webhook early (when Buy Now button is clicked)
      // This tracks the initial checkout intent before contact details collection
      console.log('[Buy Now] Sending early InitiateCheckout webhook (button clicked)...');
      sendInitiateCheckoutWebhook(cart, localeInfo, {}, null);

      // Collect contact details before checkout
      console.log('[Buy Now] Collecting contact details...');
      let contactDetails = { email: '', phone: '' };
      
      try {
        contactDetails = await collectContactDetails();
        console.log('[Buy Now] ✅ Contact details collected:', { email: contactDetails.email ? '***' : '', phone: contactDetails.phone ? '***' : '' });
      } catch (error) {
        // User cancelled contact details collection
        console.log('[Buy Now] User cancelled contact details collection');
        hideButtonLoading(button);
        return;
      }

      // Build checkout redirect URL for webhook (same format as cart checkout)
      const checkoutRedirectURL = buildCheckoutRedirectURL(cart, localeInfo, contactDetails);
      console.log('[Buy Now] Checkout redirect URL:', checkoutRedirectURL);

      // Create Stripe checkout session first to get the checkout URL
      console.log('[Buy Now] Creating Stripe checkout session...');
      const checkoutUrl = await createStripeCheckoutSession(cart, localeInfo, contactDetails);

      // Send InitiateCheckout webhook again with complete data (contact details + checkout URL)
      // This tracks the final checkout initiation when user clicks "Proceed to Payment"
      // (truly fire and forget - don't await or catch)
      console.log('[Buy Now] Sending InitiateCheckout webhook with complete data (Proceed to Payment clicked)...');
      sendInitiateCheckoutWebhook(cart, localeInfo, contactDetails, checkoutRedirectURL);

      // Close the contact details dialog just before redirect
      // This keeps the loading state visible until redirect happens
      if (window.ContactDetailsCollector && typeof window.ContactDetailsCollector.closeDialog === 'function') {
        try {
          window.ContactDetailsCollector.closeDialog();
        } catch (closeError) {
          console.warn('[Buy Now] Error closing contact details dialog:', closeError);
        }
      }
      
      // Small delay to allow dialog close animation (optional, but improves UX)
      await new Promise(resolve => setTimeout(resolve, 50));

      // Redirect to Stripe Checkout
      console.log('[Buy Now] Redirecting to Stripe Checkout:', checkoutUrl);
      window.location.href = checkoutUrl;

    } catch (error) {
      console.error('[Buy Now] Error:', error);
      hideButtonLoading(button);
      
      // Show user-friendly error message
      const errorMessage = error.message || 'Unable to process Buy Now. Please try again.';
      alert(errorMessage);
    }
  }

  // Initialize Buy Now buttons
  function initBuyNowButtons() {
    const buyNowButtons = document.querySelectorAll('[data-buy-now-button]');
    
    buyNowButtons.forEach(button => {
      if (button.dataset.buyNowHandlerAttached) {
        return; // Already initialized
      }
      
      button.dataset.buyNowHandlerAttached = 'true';
      button.addEventListener('click', handleBuyNow, true);
    });
  }

  // Set up event delegation
  function setupEventDelegation() {
    if (!document.body) {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', setupEventDelegation);
      } else {
        setTimeout(setupEventDelegation, 50);
      }
      return;
    }

    // Use capture phase for early interception
    document.body.addEventListener('click', function(e) {
      const button = e.target.closest('[data-buy-now-button]');
      if (button) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        handleBuyNow({
          preventDefault: () => {},
          stopPropagation: () => {},
          stopImmediatePropagation: () => {},
          currentTarget: button,
          target: button
        });
        return false;
      }
    }, true);
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      setupEventDelegation();
      initBuyNowButtons();
      setTimeout(initBuyNowButtons, 200);
    });
  } else {
    setupEventDelegation();
    initBuyNowButtons();
    setTimeout(initBuyNowButtons, 200);
  }

  // Watch for dynamically added buttons
  if (typeof MutationObserver !== 'undefined') {
    const observer = new MutationObserver(function(mutations) {
      const hasNewButtons = mutations.some(mutation => 
        Array.from(mutation.addedNodes).some(node => 
          node.nodeType === 1 && (
            (node.hasAttribute && node.hasAttribute('data-buy-now-button')) ||
            (node.querySelector && node.querySelector('[data-buy-now-button]'))
          )
        )
      );
      
      if (hasNewButtons) {
        initBuyNowButtons();
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  // Additional delayed initialization
  setTimeout(initBuyNowButtons, 1000);
})();
